import React from 'react';
import { useTranslation } from 'react-i18next';

import { useReducedMotion } from '@/nova/hooks/useReducedMotion';
import { GAME_LIST_FILTER } from '@/nova/theme/tokens';
import { scrollParentOf, scrollTopOf } from '../../blog-editor/hooks/useSortableRows';
import {
  filterTerms,
  firstMatchFromHere,
  gameKey,
  gameMatches,
  matchFromScroll,
  providerLabelOf,
  stepMatch,
  type MatchSpan,
  type SiteGame,
} from '../site-games';

export type GameListFilterOptions = {
  games: SiteGame[];
  providerNames: Record<string, string>;
  /** Render the list at least as far as `index`, so a match past the rendered window can be scrolled to. */
  reveal: (index: number) => void;
  /** Holds the games' elements, each marked with its game's `data-id`. */
  containerRef: React.RefObject<HTMLElement>;
};

export type GameListFilterState = {
  query: string;
  onQueryChange: (value: string) => void;
  /** The ids that match, or null while nothing is filtered. */
  matches: ReadonlySet<string> | null;
  /** The match the page last jumped to (highlighted), if any. */
  currentMatch: string | null;
  /** Its position among the matches (-1 without one), and how many there are. */
  current: number;
  total: number;
  previous: () => void;
  next: () => void;
  /** Drop the current match and its highlight (a game's menu opened, so attention moved on). */
  clearCurrent: () => void;
};

const elementsById = (container: HTMLElement): Map<string, HTMLElement> =>
  new Map(
    Array.from(container.querySelectorAll<HTMLElement>('[data-id]')).map((el) => [el.dataset.id ?? '', el] as const),
  );

/**
 * Where each of `ids` sits relative to the landing line (a scrolled-to match's top sits on it: its scroll
 * margin below the top of the area that scrolls the list), or null when it is not rendered.
 *
 * Measured from the top of that scroll area, not the screen: on desktop that area is PageShell's slot and in
 * a dialog its scroll area, both starting below the top of the screen.
 */
const landingSpans = (container: HTMLElement, ids: string[]): (MatchSpan | null)[] => {
  const elements = elementsById(container);
  const scroller = scrollParentOf(container);
  const scrollportTop = scroller ? scroller.getBoundingClientRect().top + scroller.clientTop : 0;
  return ids.map((id) => {
    const el = elements.get(id);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const line = scrollportTop + (parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0);
    return { top: rect.top - line, bottom: rect.bottom - line };
  });
};

/**
 * The live filter of a game list: which games match, and moving the page between them.
 *
 * Every keystroke jumps to the first match from where the page is, on down (wrapping to the first match), so
 * refining a filter mid-list never throws the page back to the top. Previous / next go from where the page
 * is too: right after a jump, and for as long as the page stays where that jump left it, they step from that
 * match (so two matches on
 * the same grid row, or one the page cannot scroll up to, are not skipped); once the page has been scrolled
 * away, they go to the first match below / the last match above the line matches land on. Either way a
 * match past the rendered window is rendered first, then scrolled to just below the pinned filter.
 */
export const useGameListFilter = ({
  games,
  providerNames,
  reveal,
  containerRef,
}: GameListFilterOptions): GameListFilterState => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [query, setQuery] = React.useState('');
  const [currentMatch, setCurrentMatch] = React.useState<string | null>(null);
  // Bumped by every jump, so the page scrolls to the match even when it is the same game as before; and whether
  // that scroll may glide (a step) or must land at once (a keystroke, see the scroll effect).
  const [scrollRequest, setScrollRequest] = React.useState({ count: 0, glide: true });
  // Where the page came to rest after the last jump; null while it is still getting there.
  const settledScrollRef = React.useRef<number | null>(null);
  const settleTimerRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => () => window.clearTimeout(settleTimerRef.current), []);

  const matchesFor = React.useCallback(
    (text: string): string[] => {
      const terms = filterTerms(text);
      if (terms.length === 0) return [];
      return games.filter((game) => gameMatches(game, providerLabelOf(game, providerNames, t), terms)).map(gameKey);
    },
    [games, providerNames, t],
  );
  const matchIds = React.useMemo(() => matchesFor(query), [matchesFor, query]);
  const filtering = filterTerms(query).length > 0;
  const matches = React.useMemo(() => (filtering ? new Set(matchIds) : null), [filtering, matchIds]);

  const goToMatch = (id: string | null, glide = true): void => {
    setCurrentMatch(id);
    window.clearTimeout(settleTimerRef.current);
    settledScrollRef.current = null;
    if (id === null) return;
    reveal(games.findIndex((game) => gameKey(game) === id));
    setScrollRequest((current) => ({ count: current.count + 1, glide }));
    settleTimerRef.current = window.setTimeout(() => {
      const container = containerRef.current;
      settledScrollRef.current = container ? scrollTopOf(scrollParentOf(container)) : null;
    }, GAME_LIST_FILTER.JUMP_SETTLE_MS);
  };

  const step = (direction: 1 | -1): void => {
    if (matchIds.length === 0) return;
    const container = containerRef.current;
    if (currentMatch !== null && matchIds.includes(currentMatch) && container) {
      const settled = settledScrollRef.current;
      if (settled === null || Math.abs(scrollTopOf(scrollParentOf(container)) - settled) <= 2) {
        goToMatch(stepMatch(matchIds, currentMatch, direction));
        return;
      }
    }
    const spans = container ? landingSpans(container, matchIds) : matchIds.map(() => null);
    goToMatch(
      matchFromScroll(
        matchIds,
        spans.map((span) => (span ? span.top : null)),
        direction,
      ),
    );
  };

  const clearCurrent = React.useCallback((): void => {
    setCurrentMatch(null);
    window.clearTimeout(settleTimerRef.current);
    settledScrollRef.current = null;
  }, []);

  // Scroll the jumped-to match to just below the floating filter (the games' scroll margin clears it). A
  // keystroke's jump lands at once: typing makes the browser scroll the caret into view, which cancels a smooth
  // scroll under way in the same scroll area (seen in the preview sheet, where the field pins flush to the top),
  // and the page would stop short of the match. Previous / next glide.
  React.useEffect(() => {
    if (scrollRequest.count === 0 || currentMatch === null) return;
    const target = containerRef.current ? elementsById(containerRef.current).get(currentMatch) : undefined;
    const behavior = scrollRequest.glide && !reducedMotion ? 'smooth' : 'auto';
    target?.scrollIntoView({ block: 'start', behavior });
  }, [scrollRequest, currentMatch, reducedMotion, containerRef]);

  return {
    query,
    onQueryChange: (value: string): void => {
      setQuery(value);
      const next = matchesFor(value);
      const container = containerRef.current;
      goToMatch(container ? firstMatchFromHere(next, landingSpans(container, next)) : next[0] ?? null, false);
    },
    matches,
    currentMatch,
    current: currentMatch === null ? -1 : matchIds.indexOf(currentMatch),
    total: matchIds.length,
    previous: () => step(-1),
    next: () => step(1),
    clearCurrent,
  };
};

export default useGameListFilter;
